import { QueryClient, QueryState } from '@tanstack/react-query';
import { runAsBackgroundRequest } from 'services/backgroundRequest';

const MARKED = Symbol('backgroundAware');

/**
 * Has this query fetched before (or is this a retry)? Then this fetch is a
 * poll, a focus/reconnect refetch, a retry or a refresh after a save — not
 * the admin opening the page.
 */
export const isRefetch = (state?: QueryState<unknown, unknown>): boolean =>
  !!state &&
  (state.dataUpdateCount > 0 ||
    state.errorUpdateCount > 0 ||
    state.fetchFailureCount > 0);

/**
 * The first load of a query is the admin's own page load: if it fails, the
 * request layer shows its error toast as before. Every later fetch of the same
 * query runs as a background request (services/backgroundRequest), so it
 * never toasts or redirects. A query that is background from its very first
 * fetch (a sidebar badge, the role sync) says so with
 * `meta: { background: true }`.
 */
class AdminQueryClient extends QueryClient {
  defaultQueryOptions(options: any): any {
    const defaulted: any = super.defaultQueryOptions(options);
    const queryFn = defaulted.queryFn;
    if (typeof queryFn !== 'function' || queryFn[MARKED]) return defaulted;

    const backgroundAware = (context: any) => {
      const background =
        context?.meta?.background === true ||
        isRefetch(this.getQueryState(context?.queryKey));
      return background
        ? runAsBackgroundRequest(() => queryFn(context))
        : queryFn(context);
    };
    backgroundAware[MARKED] = true;
    return { ...defaulted, queryFn: backgroundAware };
  }
}

export const queryClient = new AdminQueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      cacheTime: 1000 * 60 * 10, // 10 minutes
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: 1,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      retry: false,
    },
  },
});
